import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useClerk, useUser } from '@clerk/clerk-react'
import { SplashScreen } from '@capacitor/splash-screen'
import { LiveUpdate } from '@capawesome/capacitor-live-update'
import './account.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import Avatar from '@components/molecules/avatar'
import Divider from '@components/atoms/divider'
import Spinner from '@components/atoms/spinner'
import Switch from '@components/atoms/switch'

// ROUTES
import { DashboardRoute } from './dashboard'
import { ProfileRoute } from './profile'
import { PMRoute } from './pm'
import { UserPhysicalCardRoute } from './physical-card'
import { UserReloadableCardRoute } from './reloadable-card'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { AuthAPI } from '@apis'
import { links } from '@constants'
import { flatten } from '@utils/string-helper'
import * as Sentry from "@sentry/react"
import { toggleBodyScroll } from '@utils/temp-helpers'
import { NativeBridge } from '@capacitor/native-bridge'
import { Preferences } from '@capacitor/preferences'
import { createAuthenticatedLoader } from '@utils/loaders'
import { platform } from '@utils/capacitor-helper'
import { clearLastLaunchUrl } from '@utils/launch-url-storage'
import { getRequiredOnboardingPath } from '@utils/user-onboarding'

import { version } from '../../../package.json'

const accountRouteId = "account"

const Layout: FC = () => {
    const { signOut } = useClerk()
    const { isLoaded, user } = useUser()
    const navigate = useNavigate()

    const [isBeta, setIsBeta] = useState(false)

    // Load beta preference on mount
    useEffect(() => {
        async function loadBetaPreference() {
            try {
                const { value } = await Preferences.get({ key: 'beta' })
                setIsBeta(value === 'true')
            } catch (error) {
                console.error('Error loading beta preference:', error)
            }
        }
        if (platform !== 'web') {
            loadBetaPreference()
        }
    }, [])

    async function handleBetaToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const newValue = e.target.checked
        setIsBeta(newValue)

        try {
            // Save the preference
            await Preferences.set({
                key: 'beta',
                value: newValue.toString()
            })
            console.log('Beta preference saved:', newValue)

            // Restart the app to apply the new channel
            console.log('[ LiveUpdate ] [ resume ] reload()')
            await SplashScreen.show()
            await LiveUpdate.reload()
        } catch (error) {
            console.error('Error saving beta preference:', error)
        }
    }

    useEffect(() => {
        if (isLoaded && user) {
            const targetPath = getRequiredOnboardingPath(user, document.location.search)
            const currentPath = `${document.location.pathname}${document.location.search}`

            if (targetPath && targetPath !== currentPath) {
                Sentry.addBreadcrumb({ category: 'account', message: 'missing required onboarding data, redirecting', level: 'info', data: { targetPath } })
                navigate(targetPath, { replace: true })
            }
        }
    }, [isLoaded, user, navigate])

    // Temp Drawer logic for screen size
    const [screenSize] = useWindowDimensions()
    const [isOpen, setIsOpen] = useState(screenSize === 'large')
    useEffect(() => { if (screenSize === 'large') setIsOpen(true) }, [screenSize])
    function toggleDrawer() {
        setIsOpen(screenSize === 'large' ? true : !isOpen)
    }

    // Disable pull-to-refresh and body scroll when drawer is open
    useEffect(() => {
        if (screenSize !== 'large') {
            if (isOpen) {
                toggleBodyScroll(true)
                NativeBridge.disablePullToRefresh().catch(() => { })
            } else {
                toggleBodyScroll(false)
                NativeBridge.enablePullToRefresh().catch(() => { })
            }
        }
    }, [isOpen, screenSize])

    // Touch gesture handling for drawer
    const drawerRef = useRef<HTMLElement>(null)
    const touchStartX = useRef<number>(0)
    const touchCurrentX = useRef<number>(0)
    const [dragOffset, setDragOffset] = useState(0)

    useEffect(() => {
        const drawer = drawerRef.current
        if (!drawer || screenSize === 'large') return

        const handleTouchStart = (e: TouchEvent) => {
            touchStartX.current = e.touches[0].clientX
            touchCurrentX.current = e.touches[0].clientX
        }

        const handleTouchMove = (e: TouchEvent) => {
            touchCurrentX.current = e.touches[0].clientX
            const diff = touchCurrentX.current - touchStartX.current

            // Only allow dragging left (closing)
            if (diff < 0) {
                setDragOffset(diff)
            }
        }

        const handleTouchEnd = () => {
            const diff = touchCurrentX.current - touchStartX.current

            // Close drawer if swiped left more than 50px
            if (diff < -50) {
                setIsOpen(false)
            }

            // Reset drag offset
            setDragOffset(0)
            touchStartX.current = 0
            touchCurrentX.current = 0
        }

        drawer.addEventListener('touchstart', handleTouchStart)
        drawer.addEventListener('touchmove', handleTouchMove)
        drawer.addEventListener('touchend', handleTouchEnd)

        return () => {
            drawer.removeEventListener('touchstart', handleTouchStart)
            drawer.removeEventListener('touchmove', handleTouchMove)
            drawer.removeEventListener('touchend', handleTouchEnd)
        }
    }, [screenSize])

    const [isLoading, setIsLoading] = useState(false)
    const ref = useRef<HTMLDialogElement>(null)
    useEffect(() => {
        if (ref.current) {
            if (isLoading) ref.current.showModal()
            else ref.current.close()
        }
    }, [isLoading])

    async function handleSignOut() {
        setIsLoading(true)
        const redirect = `/sign-in`

        await AuthAPI.RevokeSessionRequest.fetch({})
            .catch((error) => {
                console.error(error)
            })

        await clearLastLaunchUrl().catch((error) => console.error('[ clearLastLaunchUrl ]', error))
        setIsLoading(false)
        signOut({ redirectUrl: redirect }).catch((err) => console.log(err))
    }

    return (<>
        <Column className="account-stack">
            {screenSize !== 'large' && (
                <Column.Header.Start>
                    <div onClick={toggleDrawer} style={{ cursor: 'pointer' }}>
                        {isOpen
                            ? <Icons.Close />
                            : <Icons.NavigationMenu />
                        }
                    </div>
                </Column.Header.Start>
            )}
            <Outlet />
        </Column>

        {/* Drawer and background moved outside of Column */}
        {(screenSize !== 'large') && <div className={`temp-drawer-background ${isOpen ? 'open' : ''}`} onClick={toggleDrawer} />}
        <aside
            ref={drawerRef}
            className={`temp-drawer ${isOpen ? 'open' : ''} ${isBeta ? 'beta' : ''}`}
            style={{
                transform: dragOffset !== 0
                    ? `translateX(${dragOffset}px)`
                    : undefined,
                transition: dragOffset !== 0 ? 'none' : undefined
            }}
        >
            <nav>
                {isBeta && <div className="beta-banner"><small><strong>DEVELOPER PREVIEW - BETA MODE ACTIVE</strong></small></div>}
                <div className="flex flex-row items-center gap-4">
                    <Avatar user={user} />
                    <p className='text-white'><strong>{`${user?.firstName} ${user?.lastName}`.trim()}</strong></p>
                </div>
                <NavLink replace to="/account" onClick={toggleDrawer} end className={({ isActive }) => flatten("nav-link", { active: isActive })}>
                    <Icons.Group />
                    <p>Groups</p>
                </NavLink>
                <NavLink replace to="/account/profile" onClick={toggleDrawer} className={({ isActive }) => flatten("nav-link", { active: isActive })}>
                    <Icons.UserIcon />
                    <p>Profile</p>
                </NavLink>
                {(!!user?.publicMetadata.tera?.physicalCard?.id) &&
                    <NavLink replace to="/account/physical-card" onClick={toggleDrawer} className={({ isActive }) => flatten("nav-link", { active: isActive })}>
                        <Icons.PhysicalCard />
                        <p>My Physical Card</p>
                    </NavLink>
                }
                {(!!user?.publicMetadata.tera?.virtualCard?.id) &&
                    <NavLink replace to="/account/reloadable-card" onClick={toggleDrawer} className={({ isActive }) => flatten("nav-link", { active: isActive })}>
                        <Icons.PhysicalCard />
                        <p>My Reloadable Card</p>
                    </NavLink>
                }
                <NavLink replace to="/account/pm" onClick={toggleDrawer} className={({ isActive }) => flatten("nav-link", { active: isActive })}>
                    <Icons.UnknownPM />
                    <p>Payment Methods</p>
                </NavLink>
                <a onClick={handleSignOut} className="nav-link">
                    <Icons.LogOut />
                    <p>Log out</p>
                </a>
                <a onClick={() => window.open(links.contactUs, '_blank')}>Contact Us</a>
                <Link to="/group/demo" onClick={toggleDrawer}>How It Works</Link>
                <a onClick={() => window.open(links.privacyPolicy, '_blank')}>Privacy Policy</a>
                <Link to="/notice">Purchase Notice</Link>
                <a onClick={() => window.open(links.termsOfUse, '_blank')}>Terms of Service</a>
                <a onClick={() => window.open(links.FAQs, '_blank')}>FAQs</a>
                {(platform !== 'web' && user?.publicMetadata.betaUser) && <>
                    <Divider style={{ opacity: 0.5 }} />
                    <div className="flex flex-row items-center justify-between">
                        <div className="flex flex-row items-center gap-2.5">
                            <Icons.Beta tint="var(--splitcard-yellow-beta)" width="1.25rem" height="1.25rem" />
                            <div className="flex flex-col">
                                <p className="text-body-2 text-white"><strong>Beta</strong></p>
                                <small className="text-gray-200">Test unreleased features</small>
                            </div>
                        </div>
                        <Switch checked={isBeta} onChange={handleBetaToggleChange} beta={true} />
                    </div>
                    <Divider style={{ opacity: 0.5 }} />
                </>}
                <p className="text-body-2" style={{ color: 'white' }}>Version: {version}</p>
            </nav>
        </aside >

        <dialog ref={ref}>
            <div>
                <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <Spinner />
                </div>
            </div>
        </dialog>
    </>)
}

export type ScreenSize = "large" | "medium" | "small"
type ArrayType<RT> = RT extends 'pixels' ? [number, number] : [ScreenSize, number]

/**
 * Creates an event lister that observes changes to the window's dimensions and returns those values in the form of an array.
 * 
 * @param {object} opts Contains options that modifies certian portions of the hook.
 * @param {'dimensions' | 'pixels' | undefined} opts.returnType Influences the return value of the hook. Value defaults to `dimensions` if undefined.
 *                                                              - `dimensions` returns [ScreenSize, number]
 *                                                              - `pixels` returns [number, number]
 * @returns {ArrayType<RT>} Window dimensions cast to an array in positions: [`window-width`, `window-height`]
 */
export function useWindowDimensions<RT extends 'dimensions' | 'pixels' | undefined = undefined>(opts?: { returnType?: RT }): ArrayType<RT> {
    const { returnType = 'dimensions' } = opts || {}
    const [size, setSize] = useState<[ScreenSize | number, number]>([returnType === 'dimensions' ? getScreenSize(window.innerWidth) : window.innerWidth, window.innerHeight])

    /** We use LayoutEffect here because the window isn't a part of the DOM and isnt't painted, so we can get away with . */
    useLayoutEffect(() => {
        function updateSize() {
            setSize([returnType === 'dimensions' ? getScreenSize(window.innerWidth) : window.innerWidth, window.innerHeight])
        }
        updateSize()
        window.addEventListener('resize', updateSize)
        return () => window.removeEventListener('resize', updateSize)
    }, [returnType, setSize])

    return size as ArrayType<RT>
}
/**
 * Converts a pixel value into a `ScreenSize` string.
 * 
 * @param {number} value - The pixel value to be evaluated.
 * @returns {ScreenSize} The abstract categorization of varying pixel values, in the form of a `ScreenSize` string.
 */
export const getScreenSize = (value: number): ScreenSize => {
    return value > 1024
        ? "large"
        : value < 625
            ? "small"
            : "medium"
}

const loader = createAuthenticatedLoader(() => null)

const AccountRoute: RouteObject = {
    id: accountRouteId,
    path: "/account",
    element: <Layout />,
    loader,
    children: [
        DashboardRoute,
        ProfileRoute,
        UserPhysicalCardRoute,
        UserReloadableCardRoute,
        PMRoute,
    ]
}

export default AccountRoute
